import { Card, Colors, Icon, Text, type ColorType, type IconName } from '@amelio/ui-library';

import type { ReactNode } from 'react';

interface InfoCalloutProps {
    /** The message in full weight, read first. */
    title: ReactNode;
    /** The explanation, in the muted colour. */
    description?: ReactNode;
    /** A "tip" line: a bold label followed by its text. */
    tip?: { label: ReactNode; description: ReactNode };
    /** Library icon name; the information circle by default. */
    iconName?: IconName;
    /** Icon colour; a neutral grey by default. */
    iconColor?: ColorType | 'inherit';
    /** Icon pixel size; `lg` (24px) by default. */
    iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /**
     * Wraps the icon in a chip with this className (e.g. a bordered circle) instead of rendering it
     * bare. Omitted by default, which keeps the existing bare-icon look for current consumers.
     */
    iconWrapperClassName?: string;
    /** The title's font weight; `regular` by default. */
    titleWeight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
    /** Card background; a translucent white by default. */
    bgColor?: string;
    /** Card border colour; white by default. */
    borderColor?: string;
    /** Card corner radius; `3xl` by default. */
    borderRadius?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
    className?: string;
    dataTestId?: string;
}

/**
 * A translucent informational card: an icon, a lead line, an optional explanation and an optional
 * tip. Used by the evaluator-selection step and by the confidentiality notice of the growth tab.
 */
const InfoCallout = ({
    title,
    description,
    tip,
    iconName = 'essential-info-circle',
    iconColor = Colors.BASE_500,
    iconSize = 'lg',
    iconWrapperClassName,
    titleWeight = 'regular',
    bgColor = 'bg-white/50',
    borderColor = 'white',
    borderRadius = '3xl',
    className = 'mt-6 max-w-3xl px-4 py-8',
    dataTestId = 'info-callout',
}: InfoCalloutProps) => {
    const icon = (
        <Icon
            name={iconName}
            size={iconSize}
            color={iconColor}
            ariaHidden
        />
    );

    return (
        <Card
            className={className}
            borderRadius={borderRadius}
            bgColor={bgColor}
            borderColor={borderColor}
            padding={'none'}
            dataTestId={dataTestId}
        >
            <div className={'flex items-start gap-3'}>
                {iconWrapperClassName ? <div className={iconWrapperClassName}>{icon}</div> : icon}

                <div className={'flex-1'}>
                    <Text
                        weight={titleWeight}
                        size={'small'}
                        className={description || tip ? 'mb-3' : undefined}
                    >
                        {title}
                    </Text>

                    {description && (
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={tip ? 'mb-3' : undefined}
                        >
                            {description}
                        </Text>
                    )}

                    {tip && (
                        <div className={'flex gap-1'}>
                            <Text
                                size={'small'}
                                weight={'bold'}
                                color={Colors.BASE_400}
                            >
                                {tip.label}
                            </Text>

                            <Text
                                size={'small'}
                                color={Colors.BASE_400}
                            >
                                {tip.description}
                            </Text>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export { InfoCallout };
export type { InfoCalloutProps };
